import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { CTADropdown } from "../../../components/shared/Dropdown";
import { ButtonPrimary, ButtonPrimary1 } from "../../../components/utils";
import { useRejectRecommendedChanges } from "../../../queries/grantApproval";
import { GrantRecommendation } from "../../../api/grantApproval";

// Fields that can be proposed via recommend-change: `key` matches the key
// used in proposedChanges, `wizardStep` is which step of the grant wizard
// (/options/grant/edit/:id) it lives on.
export const PROPOSED_CHANGE_FIELDS: {
  key: string;
  label: string;
  wizardStep: number;
}[] = [{ key: "optionsGranted", label: "No of Options", wizardStep: 2 }];

const RECOMMENDATION_SUMMARY_FIELDS = [
  { key: "optionsGranted", label: "Options" },
  { key: "valueOfOptions", label: "Value" },
] as const;

// Shared action logic (reject / navigate-to-edit) so both the compact,
// per-approver-row view (GrantApprovalCard) and the standalone bulk-dialog
// view (RecommendedChangesAdmin) resolve a recommendation identically.
// `onNavigate` fires right before navigating away — callers embedded inside
// a Dialog pass their onClose here so the dialog doesn't stay open behind
// the grant-edit page.
export function useRecommendationActions(
  grantId: string,
  onNavigate?: () => void,
  onResolved?: () => void
) {
  const navigate = useNavigate();
  const { mutateAsync: rejectChangesFn } = useRejectRecommendedChanges();
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const reject = async (rec: GrantRecommendation) => {
    setRejectingId(rec.grantApprovalId);
    try {
      await rejectChangesFn({ grantId, signOffId: rec.grantApprovalId });
      toast("Recommendation rejected successfully!", {
        type: "success",
        autoClose: 2000,
      });
      onResolved?.();
    } catch (err: any) {
      toast(err?.message || "Failed to reject recommendation", {
        type: "error",
        autoClose: 2000,
      });
    } finally {
      setRejectingId(null);
    }
  };

  const useAndEditGrant = (rec: GrantRecommendation) => {
    const proposedChanges = rec.proposedChanges ?? {};
    const initialStep =
      proposedChanges.optionsGranted !== undefined ||
      proposedChanges.valueOfOptions !== undefined
        ? 2
        : 4;
    onNavigate?.();
    navigate(`/options/grant/edit/${grantId}`, {
      state: {
        initialStep,
        recommendationContext: {
          signOffId: rec.grantApprovalId,
          proposedChanges,
        },
      },
    });
  };

  return { reject, useAndEditGrant, rejectingId };
}

// Compact actions for a recommendation that's already identified elsewhere
// (e.g. the approver row it came from already shows the approver's name,
// timing, and comment) — just the proposed value(s) plus Use & Edit / Reject.
export function RecommendationInlineActions({
  grantId,
  recommendation,
  onNavigate,
  onResolved,
}: {
  grantId: string;
  recommendation: GrantRecommendation;
  onNavigate?: () => void;
  onResolved?: () => void;
}) {
  const { reject, useAndEditGrant, rejectingId } = useRecommendationActions(
    grantId,
    onNavigate,
    onResolved
  );

  return (
    <HStackActions
      recommendation={recommendation}
      rejectingId={rejectingId}
      onReject={reject}
      onUseAndEdit={useAndEditGrant}
    />
  );
}

function HStackActions({
  recommendation,
  rejectingId,
  onReject,
  onUseAndEdit,
}: {
  recommendation: GrantRecommendation;
  rejectingId: string | null;
  onReject: (rec: GrantRecommendation) => void;
  onUseAndEdit: (rec: GrantRecommendation) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 mt-2">
      <div className="flex items-center gap-3">
        {RECOMMENDATION_SUMMARY_FIELDS.map((f) =>
          recommendation.proposedChanges?.[f.key] !== undefined ? (
            <span key={f.key} className="text-xs text-primaryText">
              <span className="text-gray-500">{f.label}: </span>
              <span className="font-semibold">
                {String(recommendation.proposedChanges[f.key])}
              </span>
            </span>
          ) : null
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <ButtonPrimary onClick={() => onUseAndEdit(recommendation)}>
          Use & Edit Grant
        </ButtonPrimary>
        <ButtonPrimary1
          loading={rejectingId === recommendation.grantApprovalId}
          onClick={() => onReject(recommendation)}
        >
          Reject
        </ButtonPrimary1>
      </div>
    </div>
  );
}

export function RecommendationActionsMenu({
  grantId,
  recommendation,
  onNavigate,
  onResolved,
}: {
  grantId: string;
  recommendation: GrantRecommendation;
  onNavigate?: () => void;
  onResolved?: () => void;
}) {
  const { reject, useAndEditGrant, rejectingId } = useRecommendationActions(
    grantId,
    onNavigate,
    onResolved
  );
  const isRejecting = rejectingId === recommendation.grantApprovalId;

  return (
    <CTADropdown
      className="h-full w-full items-center justify-center"
      dropdownClassName="!z-[1400]"
      render={() => (
        <Icon
          icon="carbon:overflow-menu-horizontal"
          className="rounded text-gray-400 hover:bg-gray-300 mt-1"
          height={20}
          width={20}
          style={{ display: "block", flex: "0 0 20px" }}
        />
      )}
      actions={[
        { name: "Use & Edit Grant" },
        { name: "Reject", disabled: isRejecting },
      ]}
      onAction={(action) => {
        if (action.name === "Use & Edit Grant") {
          useAndEditGrant(recommendation);
        } else if (action.name === "Reject") {
          reject(recommendation);
        }
      }}
    />
  );
}
